/**
 * UpdateInterestsForm
 *
 * Form component for updating user's disciplines, industries, and keywords.
 * - Populates dropdowns from context-provided options.
 * - Initializes state from userDetails (user's current selections).
 * - Submits updated interests to backend via useInterestsUpdate hook.
 * - Displays status messages for loading, errors, and success.
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { useProfileUpdateContext } from "../../contexts/ProfileUpdateContext";
import { useInterestsUpdate } from "../../hooks/useInterestsUpdate";
import { formContainer } from "./styles";

type Option = { value: string; label: string };

/**
 * User interests data structure for form props.
 */
export type UserInterestsData = {
  disciplines?: string[] | any[];
  industries?: string[] | any[];
  keywords?: string;
};

// Define the shape of the updated data expected back from the API/hook
type UpdatedUserData = {
  disciplines: any; // Match the type expected by your asValueArray logic
  industries: any; // Match the type expected by your asValueArray logic
  keywords: string | null;
  // ... potentially other user fields
};

/**
 * Converts various input formats to a string array.
 * Handles arrays of strings, arrays of objects, and JSON strings.
 */
function asValueArray(input: any): string[] {
  if (!input) return [];
  if (Array.isArray(input)) {
    if (typeof input[0] === "string") return input as string[];
    return (input as any[]).map((o) => o?.value ?? o).filter(Boolean);
  }
  try {
    const parsed = JSON.parse(input);
    return asValueArray(parsed);
  } catch {
    return [];
  }
}

/**
 * Main form component for updating user interests.
 * Accepts disciplines, industries, and keywords directly as props.
 */
export default function UpdateInterestsForm({
  disciplines,
  industries,
  keywords,
}: UserInterestsData) {
  const { allDisciplines, allIndustries, profileData, setProfileData } =
    useProfileUpdateContext();
  const { status, clearStatus, send } = useInterestsUpdate();

  // Local state for selected disciplines, industries, and keywords
  const [selectedDisciplines, setSelectedDisciplines] = useState<string[]>([]);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState("");

  /**
   * Initializes local state from props.
   * NOTE: We keep the status.success dependency. When the backend update succeeds,
   * the central user state (the source of these props) should change. When props change,
   * this hook runs, and the form updates with the new, correct values.
   */
  useEffect(() => {
    setSelectedDisciplines(asValueArray(disciplines));
    setSelectedIndustries(asValueArray(industries));
    setKeywordInput(typeof keywords === "string" ? keywords : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disciplines, industries, keywords]); // We rely ONLY on props changing

  /**
   * Handles changes to disciplines dropdown.
   */
  const onDisciplinesChange = (e: SelectChangeEvent<string[]>) => {
    setSelectedDisciplines(
      typeof e.target.value === "string"
        ? e.target.value.split(",")
        : e.target.value
    );
  };

  /**
   * Handles changes to industries dropdown.
   */
  const onIndustriesChange = (e: SelectChangeEvent<string[]>) => {
    setSelectedIndustries(
      typeof e.target.value === "string"
        ? e.target.value.split(",")
        : e.target.value
    );
  };

  /**
   * Renders selected values as chips.
   */
  const renderSelected = (selected: string[], options: Option[]) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {selected.map((val) => {
        const label = options.find((o) => o.value === val)?.label ?? val;
        return <Chip key={val} label={label} />;
      })}
    </Box>
  );

  /**
   * Handles form submission.
   * Calls send from useInterestsUpdate hook, and critically, updates the central user state.
   */
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearStatus();
    try {
      const updated = {
        ...profileData,
        disciplines: allDisciplines.filter((d) =>
          selectedDisciplines.includes(d.value)
        ),
        industries: allIndustries.filter((i) =>
          selectedIndustries.includes(i.value)
        ),
        keywords: keywordInput
          ? keywordInput
              .split(",")
              .map((k) => ({ value: k.trim(), label: k.trim() }))
          : [],
        _id: profileData?._id ?? "",
        name: profileData?.name ?? "",
        last_name: profileData?.last_name ?? "",
        email: profileData?.email ?? "",
        description: profileData?.description ?? "",
        location: profileData?.location ?? "",
        city: profileData?.city ?? "",
        institute: profileData?.institute ?? "",
        image: profileData?.image ?? "",
        twitter: profileData?.twitter ?? "",
        linkedIn: profileData?.linkedIn ?? "",
        instagram: profileData?.instagram ?? "",
      };
      await send({
        disciplines: selectedDisciplines.join(","),
        industries: selectedIndustries.join(","),
        keywords: keywordInput,
      });
      setProfileData(updated);
    } catch (err) {
      console.error("Interests update error:", err);
    }
  };

  return (
    <Box component="form" onSubmit={onSubmit} sx={formContainer}>
      {/* Status messages */}
      {status.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {status.error}
        </Alert>
      )}
      {status.success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {status.success}
        </Alert>
      )}
      {status.loading && <CircularProgress sx={{ mb: 2 }} />}

      {/* Disciplines dropdown */}
      <FormControl fullWidth margin="normal">
        <InputLabel id="disciplines-label">Disciplines</InputLabel>
        <Select
          labelId="disciplines-label"
          multiple
          value={selectedDisciplines}
          onChange={onDisciplinesChange}
          input={<OutlinedInput label="Disciplines" />}
          renderValue={(selected) =>
            renderSelected(selected as string[], allDisciplines)
          }
        >
          {allDisciplines.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Industries dropdown */}
      <FormControl fullWidth margin="normal">
        <InputLabel id="industries-label">Industries</InputLabel>
        <Select
          labelId="industries-label"
          multiple
          value={selectedIndustries}
          onChange={onIndustriesChange}
          input={<OutlinedInput label="Industries" />}
          renderValue={(selected) =>
            renderSelected(selected as string[], allIndustries)
          }
        >
          {allIndustries.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Keywords input */}
      <TextField
        label="Keywords"
        value={keywordInput}
        onChange={(e) => setKeywordInput(e.target.value)}
        fullWidth
        margin="normal"
        multiline
        minRows={3}
      />

      {/* Submit button */}
      <Button
        variant="contained"
        type="submit"
        sx={{ mt: 2 }}
        disabled={status.loading}
      >
        {status.loading ? "Updating..." : "Update"}
      </Button>
    </Box>
  );
}
